/**
 * ContagionSkillEffect — Poison tree plague-path evolution: epidemic spread.
 *
 * Replaces Plague Vector. On activation, targets a single enemy (patient
 * zero) and applies `poison` at full intensity. Then a multi-generation
 * BFS propagates outward — each generation broadcasts to all non-poisoned
 * enemies within range via ChainTargetingSystem (mode: 'all'). Up to 3
 * generations of spread, each receiving 80% of the previous dose.
 *
 * Upgrade hooks via resolver:
 * - Jump Vector: `jumpRangeTiles` (additive, scope: 'plague_vector') — +1 tile
 * - Mutation: `generationDotBonus` (additive, scope: 'chain') — +0.1 per gen;
 *   when active, inverts intensity from 0.8^n degradation to 1.1^n amplification
 * - Vectors (tree passive): `jumpBonusDotChance` (max, tree scope) — 15% chance
 *   per hop to apply a bonus independent 2-second poison DoT at flat intensity 3
 *   (bypasses Toxicologist multiplier). `jumpBonusDotDurationMs` (max, tree scope).
 *
 * Cooldown tier: long (2000ms).
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Contagion (plague_evolved)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type {
  ChainConfig,
  ChainTarget,
  ChainTargetResult,
} from '../../../systems/ChainTargetingSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ContagionDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs?: number;
      intensity?: number;
    }): void;
  };
  readonly chainTargeting: {
    findChainTargets(
      config: ChainConfig,
      candidates: Iterable<ChainTarget>,
    ): ChainTargetResult;
  };
  readonly target: {
    getCurrentTargetId(): string;
  };
  readonly rng: {
    roll(): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ContagionConfig {
  readonly cooldownMs: number;
  readonly baseRangeTiles: number;
  readonly baseDamageRetention: number;
  readonly maxGenerations: number;
  readonly baseIntensity: number;
}

const DEFAULT_CONFIG: ContagionConfig = {
  cooldownMs: 2000,                // long cooldown tier
  baseRangeTiles: 3,
  baseDamageRetention: 0.8,
  maxGenerations: 3,
  baseIntensity: 3,                // poison status default intensity
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Flat intensity for Vectors bonus DoT — bypasses Toxicologist. */
const VECTORS_BONUS_INTENSITY = 3;

/** Record of a single target infected during spread. */
export interface InfectedTarget {
  readonly id: string;
  readonly intensity: number;
  /** Whether the Vectors bonus DoT was applied to this target. */
  readonly bonusDotApplied?: boolean;
}

/** Record of one generation of the BFS spread. */
export interface GenerationRecord {
  readonly generation: number;
  readonly multiplier: number;
  readonly targets: InfectedTarget[];
}

/** Complete spread result from a single activation. */
export interface SpreadResult {
  readonly patientZeroId: string;
  readonly patientZeroIntensity: number;
  readonly generations: GenerationRecord[];
  readonly totalInfected: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ContagionSkillEffect implements SkillEffect {
  readonly skillId = 'plague_evolved';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ContagionDeps;
  private readonly config: ContagionConfig;

  private _cooldownRemaining = 0;
  private _activationCount = 0;
  private _targetId: string | null = null;

  /** Last spread result for assertion queries. */
  private _lastSpreadResult: SpreadResult | null = null;
  private _lastChainConfigs: ChainConfig[] = [];

  constructor(deps: ContagionDeps, config: Partial<ContagionConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationCount(): number { return this._activationCount; }
  get targetId(): string | null { return this._targetId; }
  get lastSpreadResult(): SpreadResult | null { return this._lastSpreadResult; }
  get lastChainConfigs(): readonly ChainConfig[] { return this._lastChainConfigs; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._targetId = this.deps.target.getCurrentTargetId();
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationCount++;
    this._lastChainConfigs = [];

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  /**
   * Perform the multi-generation BFS spread from patient zero outward.
   *
   * Call this after activate() with the patient zero's position and all
   * nearby enemy candidates. The spread applies `poison` to each infected
   * target at the appropriate generation-scaled intensity.
   *
   * @param patientZeroX - X position of patient zero (pixel coords)
   * @param patientZeroY - Y position of patient zero (pixel coords)
   * @param candidates - All nearby enemies eligible for infection
   * @returns The complete spread result (generation records + totals)
   */
  performSpread(
    patientZeroX: number,
    patientZeroY: number,
    candidates: ChainTarget[],
  ): SpreadResult {
    const baseIntensity = this.config.baseIntensity;
    const rangeTiles = this.resolveRange();
    const perGenMultiplier = this.resolvePerGenMultiplier();

    // Apply poison to patient zero at full intensity
    this.deps.statusEffects.apply('poison', {
      sourceSkillId: 'plague_evolved',
      intensity: baseIntensity,
    });

    const infected = new Set<string>();
    if (this._targetId) infected.add(this._targetId);

    const generations: GenerationRecord[] = [];

    let currentGenSources: Array<{ id: string; x: number; y: number }> =
      [{ id: this._targetId!, x: patientZeroX, y: patientZeroY }];

    for (let gen = 1; gen <= this.config.maxGenerations; gen++) {
      const genMultiplier = Math.pow(perGenMultiplier, gen);
      const genIntensity = baseIntensity * genMultiplier;
      const genTargets: InfectedTarget[] = [];
      const nextGenSources: Array<{ id: string; x: number; y: number }> = [];

      for (const source of currentGenSources) {
        // Filter out already-infected targets before passing to chain system
        const available = candidates.filter(c => !infected.has(c.id));

        if (available.length === 0) continue;

        const chainConfig: ChainConfig = {
          originX: source.x,
          originY: source.y,
          maxTargets: available.length, // No artificial cap — take all in range
          maxRangeTiles: rangeTiles,
          damageRetention: 1.0,         // Multipliers computed externally
          mode: 'all',
          sourceEntityId: source.id,
        };

        this._lastChainConfigs.push(chainConfig);

        const chainResult = this.deps.chainTargeting.findChainTargets(
          chainConfig,
          available,
        );

        for (const hit of chainResult.targets) {
          if (infected.has(hit.target.id)) continue;

          infected.add(hit.target.id);

          this.deps.statusEffects.apply('poison', {
            sourceSkillId: 'plague_evolved',
            intensity: genIntensity,
          });

          // Vectors bonus DoT: independent roll per hop
          const bonusDotApplied = this.rollVectorsBonusDot();

          genTargets.push({
            id: hit.target.id,
            intensity: genIntensity,
            bonusDotApplied,
          });

          nextGenSources.push({
            id: hit.target.id,
            x: hit.target.x,
            y: hit.target.y,
          });
        }
      }

      generations.push({
        generation: gen,
        multiplier: genMultiplier,
        targets: genTargets,
      });

      currentGenSources = nextGenSources;
      if (currentGenSources.length === 0) break;
    }

    const totalInfected = 1 + generations.reduce(
      (sum, g) => sum + g.targets.length, 0,
    );

    const result: SpreadResult = {
      patientZeroId: this._targetId!,
      patientZeroIntensity: baseIntensity,
      generations,
      totalInfected,
    };

    this._lastSpreadResult = result;
    return result;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._targetId = null;
    this._lastSpreadResult = null;
    this._lastChainConfigs = [];
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Resolve range: base + Jump Vector additive bonus (inherited via replacesId). */
  private resolveRange(): number {
    const base = this.config.baseRangeTiles;
    const bonus = this.deps.resolver.getAdditiveEffect('plague_vector', 'jumpRangeTiles');
    return base + bonus;
  }

  /**
   * Resolve per-generation multiplier.
   *
   * Without Mutation: baseDamageRetention (0.8) — each gen degrades.
   * With Mutation (generationDotBonus > 0): 1 + bonus (1.1) — each gen amplifies.
   */
  private resolvePerGenMultiplier(): number {
    const mutationBonus = this.deps.resolver.getAdditiveEffect('chain', 'generationDotBonus');
    if (mutationBonus > 0) {
      return 1 + mutationBonus;
    }
    const resolvedTransfer = this.deps.resolver.getMaxEffect('plague', 'jumpTransferPercent');
    const retention = resolvedTransfer ?? this.config.baseDamageRetention;
    return retention;
  }

  /**
   * Roll for Vectors bonus DoT on a hop target. If the tree passive
   * `jumpBonusDotChance` is active and the roll succeeds, applies a
   * flat-intensity independent poison with sourceSkillId 'vectors_bonus'.
   * Intensity is VECTORS_BONUS_INTENSITY (3) — bypasses Toxicologist.
   *
   * @returns true if the bonus DoT was applied
   */
  private rollVectorsBonusDot(): boolean {
    const chance = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotChance', 'max',
    );
    if (chance <= 0) return false;

    if (this.deps.rng.roll() >= chance) return false;

    const durationMs = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotDurationMs', 'max',
    );

    this.deps.statusEffects.apply('poison', {
      sourceSkillId: 'vectors_bonus',
      intensity: VECTORS_BONUS_INTENSITY,
      durationMs: durationMs > 0 ? durationMs : 2000,
    });

    return true;
  }
}
